import React from "react";
import { List, Clock, ArrowUp } from "lucide-react";
import { formatReadTime } from "@/lib/utils";

export interface TocItem {
  id: string;
  title: string;
  level: number;
}

interface ArticleTocProps {
  tocItems: TocItem[];
  activeId: string;
  readingProgress: number;
  readTime: string;
  tocNavRef: React.RefObject<HTMLElement | null>;
  handleScrollTo: (e: React.MouseEvent<HTMLAnchorElement>, id: string) => void;
  scrollToTop: () => void;
}

/**
 * Sticky Article Sidebar Table of Contents with live reading progress and back-to-top controls.
 */
export const ArticleToc: React.FC<ArticleTocProps> = ({
  tocItems,
  activeId,
  readingProgress,
  readTime,
  tocNavRef,
  handleScrollTo,
  scrollToTop,
}) => {
  if (tocItems.length < 2) return null;

  return (
    <aside className="hidden lg:block w-[310px] xl:w-[350px] 2xl:w-[380px] shrink-0 sticky top-24 self-start z-20">
      <div className="rounded-2xl border border-border/80 bg-card/95 backdrop-blur-md p-5 shadow-sm flex flex-col h-[min(720px,calc(100vh-9.5rem))] max-h-[min(720px,calc(100vh-9.5rem))] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-border/60 shrink-0">
          <div className="flex items-center gap-2">
            <List size={17} className="text-primary" />
            <span className="font-mono text-[14px] uppercase tracking-wider text-foreground font-semibold">
              Table of Contents
            </span>
          </div>
          <span className="text-[12px] font-mono text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full font-medium">
            {tocItems.length}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="mb-3 shrink-0">
          <div className="flex items-center justify-between text-[13px] text-muted-foreground mb-1.5 font-mono">
            <span>READING PROGRESS</span>
            <span className="text-primary font-semibold">{readingProgress}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-150 rounded-full"
              style={{ width: `${readingProgress}%` }}
            />
          </div>
        </div>

        {/* Dedicated Separate Scroll Container */}
        <nav
          ref={tocNavRef as React.RefObject<HTMLDivElement>}
          className="toc-sidebar-scroll flex-1 min-h-0 overflow-y-auto pr-1.5 space-y-1 my-1 overscroll-contain"
        >
          {tocItems.map((item, idx) => {
            const isActive = activeId === item.id;
            return (
              <a
                key={idx}
                href={`#${item.id}`}
                onClick={(e) => handleScrollTo(e, item.id)}
                className={`group flex items-start gap-2.5 py-2 px-2.5 rounded-lg transition-all ${
                  item.level === 3 ? "pl-6 text-[13px]" : "text-[14.5px]"
                } ${
                  isActive
                    ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                }`}
              >
                <span
                  className={`font-mono text-[12px] pt-0.5 shrink-0 ${
                    isActive ? "text-primary font-bold" : "text-muted-foreground/60"
                  }`}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="leading-snug">{item.title}</span>
              </a>
            );
          })}
          {/* Safety bottom buffer */}
          <div className="h-8 shrink-0" aria-hidden="true" />
        </nav>

        {/* Footer controls: anchored visibly at bottom */}
        <div className="pt-3 mt-auto border-t border-border/60 flex items-center justify-between text-[13.5px] text-muted-foreground shrink-0 bg-card">
          <button
            type="button"
            onClick={scrollToTop}
            className="flex items-center gap-1.5 hover:text-primary transition-colors cursor-pointer font-medium"
          >
            <ArrowUp size={15} /> Back to top
          </button>
          <span className="font-mono text-[13px] flex items-center gap-1">
            <Clock size={13} className="text-primary/70" /> {formatReadTime(readTime)}
          </span>
        </div>
      </div>
    </aside>
  );
};
